import { GridColDef } from '@mui/x-data-grid-pro';
import {
  DefaultCell,
  CreatedOnCell,
  AmountCell,
  TravellersCell
} from '../../../components/OAColumn';
import { GridColumnHeader } from '../../../components/OATableComponent';
import { OA_DATE_TIME_FORMAT_FE, OA_DATE_FORMAT_FE, FLIGHT_STATUS, HOTEL_DASHBOARD_DATE_FORMAT } from '../../../constants';
import { OAStatusChip, OATypography } from '../../../components/widgets';
import DefaultCellWithIcon from '../../../components/OAColumn/DefaultCellWithIcon';
import { Box } from '@mui/material';
import { calculateNights } from '../../../helper/timeDifference';
import { TravelDateCell } from '../../../components/OAColumn/CreatedOnCell';
import { ReactComponent as AdultIcon } from '../../../assets/icons/Adult.svg';
import { ReactComponent as ChildIcon } from '../../../assets/icons/Child.svg';

type getHotelColumnsType = () => GridColDef[];

export const getHotelColumns: getHotelColumnsType = () => {
  let draft: Array<GridColDef> = [
    {
      field: 'bookingId',
      renderHeader: params => <GridColumnHeader title='Booking Id' params={params} />,
      renderCell: params => {
        return(
          <DefaultCell data={params?.value ?? '--'} />
        )
      },
      valueGetter: params => params?.row?.bookingId,
      width: 180
    },
    {
      field: 'travellers',
      renderHeader: params => <GridColumnHeader title='Travellers' params={params} />,
      renderCell: params => {
        return params?.row?.contactDetails ? (
          <TravellersCell members={[params?.row?.contactDetails]} />
        ) : (
          <TravellersCell members={params?.value} />
        );
      },
      valueGetter: params => params?.row?.travellers,
      width: 340
    },
    {
      field: 'hotelName',
      renderHeader: params => <GridColumnHeader title='Hotel Name' params={params} />,
      renderCell: params => {
        return <DefaultCell data={params?.value ?? '--'} />;
      },
      valueGetter: params => params?.row?.hotelDetails?.hotelName,
      width: 340
    },
    {
      field: 'phone',
      renderHeader: params => <GridColumnHeader title='Phone' params={params} />,
      renderCell: params => {
        return <DefaultCell data={params?.value ?? 'N/A'} />;
      },
      valueGetter: params => params?.row?.contactDetails ? params?.row?.contactDetails?.phoneNumber : 'N/A',
      width: 140
    },
    {
      field: 'hotelAddress',
      renderHeader: params => <GridColumnHeader title='Hotel Address' params={params} />,
      renderCell: params => {
        return <DefaultCell data={params?.value ?? 'N/A'} />;
      },
      valueGetter: params => `${params?.row?.hotelDetails?.cityName}, ${params?.row?.hotelDetails?.countryName}`,
      width: 250
    },
    {
      field: 'rooms',
      renderHeader: params => <GridColumnHeader title='Rooms' params={params} />,
      renderCell: params => {
        return <DefaultCell data={params?.value ?? '--'} />;
      },
      valueGetter: params => params?.row?.roomCount,
      width: 100
    },
    {
      field: 'bookingStatus',
      renderHeader: params => <GridColumnHeader title='Status' params={params} />,
      renderCell: params => {
        const statusObj: any = FLIGHT_STATUS?.find(
          (obj: any) => obj?.value === params?.row?.bookingStatus
        );
        return (
          <OAStatusChip
            status={statusObj?.value}
            label={statusObj?.label}
            variant={statusObj?.type}
          />
        );
      },
      width: 160
    },
    {
      field: 'travellerCount',
      renderHeader: params => <GridColumnHeader title='Traveller Count' params={params} />,
      renderCell: params => {
        const counts = params?.row?.travellerRoom.reduce(
          (totals: { adult: any; child: any; infant: any; }, room: { adult: any; child: any; infant: any; }) => {
              totals.adult += room.adult;
              totals.child += room.child;
              totals.infant += room.infant;
              return totals;
          },
          { adult: 0, child: 0, infant: 0 }
        );
        return (
          <Box display='flex' justifyContent='center' alignItems='center'>
            <AdultIcon />
            <OATypography variant='body2' sx={{marginLeft:'8px'}}>{counts?.adult} <span style={{color: '#5A6068'}}>|</span></OATypography>
            <ChildIcon style={{marginLeft:'8px'}}/>
            <OATypography variant='body2' sx={{marginLeft:'8px'}}>{counts?.child}</OATypography>
          </Box>
        );
      },
      width: 150
    },
    {
      field: 'travelDates',
      renderHeader: params => <GridColumnHeader title='Check In - Check Out' params={params} />,
      renderCell: params => {
        return (
          <Box>
            <TravelDateCell fromDate={params?.row?.checkInDate} toDate={params?.row?.checkOutDate} dateFormat={HOTEL_DASHBOARD_DATE_FORMAT} year={true}/>
          </Box>
        )
      },
      width: 180,
      sortable: true
    },
    {
      field: 'nights',
      renderHeader: params => <GridColumnHeader title='Nights' params={params} />,
      renderCell: params => {
        let nights = calculateNights(params?.row?.checkInDate, params?.row?.checkOutDate)
        return <DefaultCell data={nights} />;
      },
      width: 100
    },
    {
      field: 'bookedOn',
      renderHeader: params => <GridColumnHeader title='Booked On' params={params} />,
      renderCell: params => (
        <CreatedOnCell date={params?.value} dateFormat={OA_DATE_TIME_FORMAT_FE} />
      ),
      valueGetter: params => params?.row?.bookingDate ?? params?.row?.createdAt ?? 'N/A',
      width: 175,
      sortable: true
    },
    {
      field: 'amount',
      renderHeader: params => <GridColumnHeader title='Amount(INR)' params={params} />,
      renderCell: params => <AmountCell data={params?.value} />,
      valueGetter: params => params?.row?.orderAmount?.totalAmount,
      width: 120
    }
  ];
  return draft;
};

export const getItineraryColumns: getHotelColumnsType = () => {
  let draft: Array<GridColDef> = [
    {
      field: 'name',
      renderHeader: params => <GridColumnHeader title='Name' params={params} />,
      renderCell: params => {
        return params?.row?.contactDetails ? (
          <TravellersCell members={[params?.row?.contactDetails]} />
        ) : params?.row?.travellers?.length > 0 ? (
          <TravellersCell members={params?.row?.travellers} />
        ) : (
          <Box marginLeft='16px'>N/A</Box>
        );
      },
      width: 340
    },
    {
      field: 'email',
      renderHeader: params => <GridColumnHeader title='Email' params={params} />,
      renderCell: params => {
        return <DefaultCell data={params?.value ?? 'N/A'} />;
      },
      valueGetter: params => params?.row?.contactDetails ? params?.row?.contactDetails?.email : params?.row?.travellers?.[0]?.email,
      width: 200
    },
    {
      field: 'phone',
      renderHeader: params => <GridColumnHeader title='Phone' params={params} />,
      renderCell: params => {
        return <DefaultCell data={params?.value ?? 'N/A'} />;
      },
      valueGetter: params => params?.row?.contactDetails ? params?.row?.contactDetails?.phoneNumber : params?.row?.travellers?.[0]?.phoneNumber,
      width: 140
    },
    {
      field: 'hotelName',
      renderHeader: params => <GridColumnHeader title='Hotel Name' params={params} />,
      renderCell: params => {
        return <DefaultCell data={params?.value ?? '--'} />;
      },
      valueGetter: params => params?.row?.hotelDetails?.hotelName,
      width: 340
    },
    {
      field: 'rooms',
      renderHeader: params => <GridColumnHeader title='Rooms' params={params} />,
      renderCell: params => {
        return <DefaultCell data={params?.value ?? '--'} />;
      },
      valueGetter: params => params?.row?.roomCount,
      width: 100
    },
    {
      field: 'bookingStatus',
      renderHeader: params => <GridColumnHeader title='Status' params={params} />,
      renderCell: params => {
        const statusObj: any = FLIGHT_STATUS?.find(
          (obj: any) => obj?.value === params?.row?.bookingStatus
        );
        return (
          <OAStatusChip
            status={statusObj?.value}
            label={statusObj?.label}
            variant={statusObj?.type}
          />
        );
      },
      width: 120
    },
    {
      field: 'travelDates',
      renderHeader: params => <GridColumnHeader title='Travel Dates' params={params} />,
      renderCell: params => {
        return (
          <Box>
            <TravelDateCell fromDate={params?.row?.checkInDate} toDate={params?.row?.checkOutDate} dateFormat={HOTEL_DASHBOARD_DATE_FORMAT} year={true}/>
          </Box>
        )
      },
      width: 180,
      sortable: true
    },
    {
      field: 'nights',
      renderHeader: params => <GridColumnHeader title='Nights' params={params} />,
      renderCell: params => {
        let nights = calculateNights(params?.row?.checkInDate, params?.row?.checkOutDate)
        return <DefaultCell data={nights} />;
      },
      width: 100
    },
    {
      field: 'bookedOn',
      renderHeader: params => <GridColumnHeader title='Booked On' params={params} />,
      renderCell: params => (
        <CreatedOnCell date={params?.value} dateFormat={OA_DATE_TIME_FORMAT_FE} />
      ),
      valueGetter: params => params?.row?.bookingDate ?? params?.row?.createdAt ?? 'N/A',
      width: 175,
      sortable: true
    },
    {
      field: 'amount',
      renderHeader: params => <GridColumnHeader title='Amount(INR)' params={params} />,
      renderCell: params => <AmountCell data={params?.value} />,
      valueGetter: params => params?.row?.orderAmount?.totalAmount,
      width: 120
    }
  ];
  return draft;
};
